import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import { CostChannel } from '@sg/graphql-schema';

import { type OrderChannel } from '../../../../../../machine';
import { type FeaturedUpsellProduct, type FetchProductInput } from '../../machine';
import {
  BagFeaturedUpsellProductDocument,
  type BagFeaturedUpsellProductQuery,
  type BagFeaturedUpsellProductQueryVariables,
} from './graphql/BagFeaturedUpsellProduct.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useFetchBagFeaturedUpsellProduct = () => {
  const client = useClient();

  // ───────────────────────────────────────────────────────────────────────────

  const fetchProduct = useMemo(
    () =>
      fromPromise<FeaturedUpsellProduct | undefined, FetchProductInput>(async ({ input }) => {
        const { lineageKey, restaurantId, orderChannel } = input;

        if (!lineageKey || !restaurantId) {
          return;
        }

        const response = await client
          .query<BagFeaturedUpsellProductQuery, BagFeaturedUpsellProductQueryVariables>(
            BagFeaturedUpsellProductDocument,
            { lineageKey, restaurantId, costChannel: getCostChannel(orderChannel) },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        if (response.error) {
          throw response.error;
        }

        const product = response.data?.productForRestaurantByLineageKey;

        if (!product || product.outOfStock) {
          return;
        }

        return {
          id: product.id,
          name: product.name,
          cost: product.cost,
        };
      }),
    [client],
  );

  return { fetchProduct };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCostChannel(orderChannel: OrderChannel) {
  return orderChannel === 'delivery'
    ? CostChannel.DeliveryCostChannel
    : CostChannel.DefaultCostChannel;
}
